import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../api";
import Loader from "../components/Loader";
import Breadcrumbs from "../components/Breadcrumbs";
import "../assets/buttons.css";
import "../assets/panels.css";

const BlockEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [block, setBlock] = useState(null);
  const [client, setClient] = useState(null);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchBlock = async () => {
      try {
        const response = await api.get(`/blocks/${id}`);
        if (response.data.success && response.data.block) {
          const blockData = response.data.block;
          setBlock(blockData);
          setName(blockData.name || "");

          // Fetch client details
          if (blockData.client_id) {
            try {
              const clientResponse = await api.get(`/clients/${blockData.client_id}`);
              if (clientResponse.data.client) {
                setClient(clientResponse.data.client);
              }
            } catch (clientError) {
              console.error("Error fetching client:", clientError);
            }
          }
        } else {
          toast.error("Block not found");
          navigate("/clients");
        }
      } catch (error) {
        console.error("Error fetching block:", error);
        toast.error("Failed to load block");
        navigate("/clients");
      } finally {
        setLoading(false);
      }
    };

    fetchBlock();
  }, [id, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    // Validation
    if (!name.trim()) {
      setErrors({ name: "Block name is required" });
      return;
    }

    setSubmitting(true);

    try {
      const response = await api.put(`/blocks/${id}`, {
        name: name.trim(),
      });

      if (response.data.success) {
        toast.success("Block updated successfully!");
        navigate(`/blocks/${id}`);
      } else {
        toast.error(response.data.message || "Failed to update block");
      }
    } catch (error) {
      console.error("Error updating block:", error);
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        toast.error(error.response?.data?.message || "Failed to update block");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  if (!block) {
    return (
      <div className="container">
        <div className="alert alert-warning">Block not found</div>
      </div>
    );
  }

  const breadcrumbs = [
    { label: "Dashboard", link: "/dashboard" },
    { label: "Clients", link: "/clients" },
    { label: client?.name || "Client", link: `/clients/${block.client_id}` },
    { label: block.name, link: `/blocks/${id}` },
    { label: "Edit", link: "" },
  ];

  return (
    <div className="container">
      <Breadcrumbs items={breadcrumbs} />

      <div className="row">
        <div className="col-md-12">
          <div className="panel panel-default panel-form">
            <div className="panel-heading panel-title">
              <h3>Edit Block</h3>
            </div>
            <div className="panel-body">
              <form onSubmit={handleSubmit}>
                <fieldset className="form-horizontal">
                  {/* Client (read-only) */}
                  <div className="form-group">
                    <label className="control-label col-sm-3">Client</label>
                    <div className="col-sm-5">
                      <div className="as-input">
                        {client ? (
                          <Link to={`/clients/${block.client_id}`}>{client.name}</Link>
                        ) : (
                          `Client #${block.client_id}`
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Block Name */}
                  <div className={`form-group ${errors.name ? "has-error" : ""}`}>
                    <label className="control-label col-sm-3 label-required">
                      Name
                    </label>
                    <div className="col-sm-5">
                      <input
                        type="text"
                        className="form-control"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        placeholder="Enter block name"
                      />
                      {errors.name && (
                        <span className="help-block text-danger">{errors.name}</span>
                      )}
                    </div>
                  </div>
                </fieldset>

                <fieldset className="form-horizontal mt-30">
                  <div className="col-sm-3"></div>
                  <div className="col-sm-5 text-right">
                    <Link to={`/blocks/${id}`} className="btn btn-default" style={{ marginRight: "10px" }}>
                      Cancel
                    </Link>
                    <button
                      type="submit"
                      className="btn btn-head btn-standard"
                      disabled={submitting}
                    >
                      {submitting ? "Saving..." : "Save"}
                    </button>
                  </div>
                </fieldset>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlockEdit;
